import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { CheckIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { Button, Input, Select } from "../../ui";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../../api/scholarships";
import { useEducationLevels, useFieldsOfStudy } from "../model";
import { PROFILE_KEY, STUDENT_KEY } from "./model";

const formOf = (profile) => ({
  currentLevelId: profile?.currentLevel?.id ?? "",
  fieldOfStudyId: profile?.fieldOfStudy?.id ?? "",
  institutionName: profile?.institutionName ?? "",
  programmeName: profile?.programmeName ?? "",
  gradePercent: profile?.gradePercent ?? "",
  completionYear: profile?.completionYear ?? "",
});

const THIS_YEAR = new Date().getFullYear();

const validate = (form) => {
  const errors = {};
  if (!form.currentLevelId) errors.currentLevelId = "Choose your level";
  if (!form.fieldOfStudyId) errors.fieldOfStudyId = "Choose your field of study";
  if (!form.institutionName.trim()) errors.institutionName = "Enter your school or university";
  if (!form.programmeName.trim()) errors.programmeName = "Enter your programme or subject combination";
  const grade = Number(form.gradePercent);
  if (form.gradePercent === "" || !(grade >= 0 && grade <= 100)) errors.gradePercent = "Enter a percentage from 0 to 100";
  const year = Number(form.completionYear);
  if (!(Number.isInteger(year) && year >= 1950 && year <= THIS_YEAR + 6)) errors.completionYear = "Enter the year you finished or will finish";
  return errors;
};

/**
 * The student's academic profile: what eligibility is checked against (journey step 5). Saving it
 * refreshes the scholarships they can apply for.
 */
export default function ProfileForm({ profile, onSaved }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(() => formOf(profile));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const { data: levels = [] } = useEducationLevels({ keep: [profile?.currentLevel] });
  const { data: fields = [] } = useFieldsOfStudy({ keep: [profile?.fieldOfStudy] });

  const submit = async (e) => {
    e.preventDefault();
    const found = validate(form);
    if (Object.keys(found).length) return setErrors(found);
    setSaving(true);
    try {
      const body = { ...form, institutionName: form.institutionName.trim(), programmeName: form.programmeName.trim(), gradePercent: Number(form.gradePercent), completionYear: Number(form.completionYear) };
      const { data } = await scholarshipsApi.put("student/academic-profile", body);
      queryClient.setQueryData(PROFILE_KEY, data);
      queryClient.invalidateQueries({ queryKey: STUDENT_KEY });
      toast.success("Profile saved. Scholarships you can apply for are updated.");
      onSaved?.(data);
    } catch (error) {
      console.error("Failed to save the academic profile:", error);
      setErrors(fieldErrorsOf(problemOf(error)));
      toast.error(errorMessageOf(error, "Failed to save your profile. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({
    name,
    value: form[name],
    onChange: (e) => {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: saving,
  });

  return (
    <form onSubmit={submit} className="grid gap-4 md:grid-cols-2">
      <Select label="Current level of study" required placeholder="Choose a level" options={levels.map((l) => ({ value: l.id, label: l.levelName }))} {...field("currentLevelId")} />
      <Select label="Field of study" required placeholder="Choose a field" options={fields.map((f) => ({ value: f.id, label: f.fieldName }))} {...field("fieldOfStudyId")} />
      <Input label="School or university" required maxLength={200} {...field("institutionName")} />
      <Input label="Programme or subject combination" required maxLength={200} placeholder="e.g. Physics-Chemistry-Biology" {...field("programmeName")} />
      <Input type="number" label="Final grade" required suffix="%" min={0} max={100} step="0.1" {...field("gradePercent")} />
      <Input type="number" label="Year completed (or the year you expect to finish)" required min={1950} max={THIS_YEAR + 6} {...field("completionYear")} />
      <div className="flex justify-end md:col-span-2">
        <Button type="submit" loading={saving} icon={<CheckIcon className="h-4 w-4" />}>
          Save profile
        </Button>
      </div>
    </form>
  );
}
